import { useState, useEffect } from 'react';
import api, { extractErrorMessage } from '../../services/api';
import { timeRangeToDays, type TimeRange } from '../../components/filters/TimeRangeFilter';
import type { CallLog, AnalyticsSummary, TrendsData, CostAnalysis, IntentPerformance } from './analyticsTypes';

export function useAnalyticsPage() {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [trendsData, setTrendsData] = useState<TrendsData | null>(null);
  const [costAnalysis, setCostAnalysis] = useState<CostAnalysis | null>(null);
  const [intentPerformance, setIntentPerformance] = useState<IntentPerformance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCalls, setExpandedCalls] = useState<Set<string>>(new Set());
  const [expandedStructuredData, setExpandedStructuredData] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [timeRange, setTimeRange] = useState<TimeRange>('week');

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      // 'all' → send a large window (backend cap is 10000)
      const days = timeRangeToDays(timeRange) ?? 10000;
      const { data } = await api.get(`/analytics/dashboard?days=${days}`);

      setSummary(data.summary);
      setTrendsData(data.trends);
      setCostAnalysis(data.cost_analysis);
      setIntentPerformance(data.intent_performance);

      const processedCalls = (data.call_logs?.calls || []).map((call: CallLog) => {
        let callSummary = null;
        if (call.analysis?.summary) {
          callSummary = {
            summary: call.analysis.summary,
            topics: call.analysis.topics || [],
            actionItems: call.analysis.actionItems || [],
            followUps: call.analysis.followUps || [],
            sentiment: call.analysis.sentiment || 'neutral',
            keyPoints: call.analysis.keyPoints || []
          };
        } else if (call.artifact?.summary) {
          callSummary = {
            summary: call.artifact.summary,
            topics: call.artifact.topics || [],
            actionItems: call.artifact.actionItems || [],
            followUps: call.artifact.followUps || [],
            sentiment: call.artifact.sentiment || 'neutral',
            keyPoints: call.artifact.keyPoints || []
          };
        }
        let structuredData = call.analysis?.structuredData ?? call.artifact?.structuredData ?? null;
        return { ...call, callSummary, structuredData };
      });
      processedCalls.sort((a: CallLog, b: CallLog) => {
        const dateA = new Date(a.startedAt || a.createdAt).getTime();
        const dateB = new Date(b.startedAt || b.createdAt).getTime();
        return dateB - dateA;
      });
      setCallLogs(processedCalls);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(extractErrorMessage(err, 'Failed to load analytics data. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const toggleCallSummary = (callId: string) => {
    const newExpanded = new Set(expandedCalls);
    if (newExpanded.has(callId)) newExpanded.delete(callId);
    else newExpanded.add(callId);
    setExpandedCalls(newExpanded);
  };

  const toggleStructuredData = (callId: string) => {
    const newExpanded = new Set(expandedStructuredData);
    if (newExpanded.has(callId)) newExpanded.delete(callId);
    else newExpanded.add(callId);
    setExpandedStructuredData(newExpanded);
  };

  const totalPages = Math.ceil(callLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCallLogs = callLogs.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => setCurrentPage(page);

  return {
    summary,
    callLogs,
    trendsData,
    costAnalysis,
    intentPerformance,
    loading,
    error,
    expandedCalls,
    expandedStructuredData,
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    itemsPerPage,
    paginatedCallLogs,
    timeRange,
    fetchAnalytics,
    toggleCallSummary,
    toggleStructuredData,
    handlePageChange,
    setTimeRange,
  };
}
